/**
 * AP-6 — CanCheckListFilterWindow (FM-WIN-17), wiederverwendbar für die zwei
 * unabhängigen Instanzen "Filter Error Frames" und "Filter Signals".
 *
 * Gruppenzeilen (`bold`) sind reine Kategorie-Überschriften und nicht selbst
 * ankreuzbar. Häkchen bereits bekannter Einträge bleiben beim Neubefüllen
 * (`setItems`) erhalten — Abgleich über `key`, wie `SetItems()` im Original.
 */

import { createButton } from '../../ui/controls';
import { createCheckboxGlyph } from '../../ui/controls/Checkbox';
import { createFloatingPanel, type FloatingPanel } from './FloatingPanel';

export interface CheckListItem {
  key: string;
  label: string;
  /** true = Kategorie-Überschrift, nicht ankreuzbar. */
  bold?: boolean;
}

export interface CheckListFilterDialogOptions {
  title: string;
  onApply: (checkedKeys: string[]) => void;
  onClear: () => void;
}

export interface CheckListFilterDialogHandle {
  panel: FloatingPanel;
  /** Ersetzt die Liste; erhält den Haken-Zustand bereits bekannter Keys. */
  setItems: (items: readonly CheckListItem[]) => void;
}

export function createCheckListFilterDialog(
  options: CheckListFilterDialogOptions,
): CheckListFilterDialogHandle {
  const panel = createFloatingPanel(options.title);

  const list = document.createElement('div');
  list.className = 'cm-checklist';

  let items: CheckListItem[] = [];
  const checkedKeys = new Set<string>();

  function render(): void {
    list.replaceChildren();
    if (items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'cm-checklist__empty';
      empty.textContent = 'Noch nichts erfasst.';
      list.append(empty);
      return;
    }
    for (const item of items) {
      const row = document.createElement('div');
      row.className = 'cm-checklist__row';
      if (item.bold) row.classList.add('cm-checklist__row--group');
      else row.classList.add('cm-checklist__row--indent');

      const check = createCheckboxGlyph();
      check.classList.add('cm-checklist__check');
      check.style.visibility = item.bold ? 'hidden' : 'visible';
      if (checkedKeys.has(item.key)) {
        check.querySelector('.cm-check__box')?.setAttribute('style', 'fill:var(--accent);stroke:var(--accent)');
        check.querySelector('.cm-check__tick')?.setAttribute('style', 'visibility:visible');
      }

      const label = document.createElement('span');
      label.textContent = item.label;
      row.append(check, label);

      if (!item.bold) {
        row.addEventListener('click', () => {
          if (checkedKeys.has(item.key)) checkedKeys.delete(item.key);
          else checkedKeys.add(item.key);
          render();
        });
      }
      list.append(row);
    }
  }

  const buttons = document.createElement('div');
  buttons.className = 'cm-panel-window__buttons';
  buttons.append(
    createButton('Apply', { accent: true, onClick: () => options.onApply(Array.from(checkedKeys)) }),
    createButton('Clear', {
      onClick: () => {
        checkedKeys.clear();
        render();
        options.onClear();
      },
    }),
  );

  panel.body.append(list, buttons);

  function setItems(nextItems: readonly CheckListItem[]): void {
    const stillKnown = new Set(nextItems.map((item) => item.key));
    for (const key of Array.from(checkedKeys)) {
      if (!stillKnown.has(key)) checkedKeys.delete(key);
    }
    items = [...nextItems];
    render();
  }

  render();
  return { panel, setItems };
}
